/**
 * Confirm Modal Component
 * 
 * Reusable confirmation dialog for delete and other destructive actions.
 * Can be used across all applications.
 */

import React, { ReactNode } from 'react';
import { AlertTriangle, Trash2, Info, CheckCircle } from 'lucide-react';
import Modal from './Modal';
import { Button } from '../Button';

export type ConfirmModalVariant = 'danger' | 'warning' | 'info' | 'success';

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | ReactNode;
  /** Additional description text */
  description?: string;
  /** Variant determines the color scheme */
  variant?: ConfirmModalVariant;
  /** Text for the confirm button */
  confirmText?: string;
  /** Text for the cancel button */
  cancelText?: string;
  /** Loading state for the confirm button */
  isLoading?: boolean;
  /** Custom icon to display */
  icon?: ReactNode;
  /** Whether to show the warning box */
  showWarning?: boolean;
  /** Custom warning message */
  warningMessage?: string;
}

const variantConfig = {
  danger: {
    icon: <Trash2 size={24} />,
    iconBg: 'bg-red-100',
    iconColor: 'text-[#c00035]',
    headerIcon: <AlertTriangle />,
    buttonVariant: 'danger' as const,
    warningBg: 'bg-red-50',
    warningBorder: 'border-red-200',
    warningText: 'text-[#c00035]',
  },
  warning: {
    icon: <AlertTriangle size={24} />,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-500',
    headerIcon: <AlertTriangle />,
    buttonVariant: 'danger' as const,
    warningBg: 'bg-orange-50',
    warningBorder: 'border-orange-200',
    warningText: 'text-orange-700',
  },
  info: {
    icon: <Info size={24} />,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    headerIcon: <Info />,
    buttonVariant: 'primary' as const,
    warningBg: 'bg-blue-50',
    warningBorder: 'border-blue-200',
    warningText: 'text-blue-700',
  },
  success: {
    icon: <CheckCircle size={24} />,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-500',
    headerIcon: <CheckCircle />,
    buttonVariant: 'primary' as const,
    warningBg: 'bg-green-50',
    warningBorder: 'border-green-200',
    warningText: 'text-green-700',
  },
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  description,
  variant = 'danger',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  icon,
  showWarning = true,
  warningMessage,
}) => {
  const config = variantConfig[variant];
  const displayIcon = icon || config.icon;

  const defaultWarningMessage = variant === 'danger'
    ? 'This action cannot be undone.'
    : undefined;

  const finalWarningMessage = warningMessage ?? defaultWarningMessage;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      icon={config.headerIcon}
      size="sm"
    >
      <div className="space-y-5">
        {/* Icon and Message */}
        <div className="text-center">
          <div className={`
            w-16 h-16 mx-auto mb-4 rounded-full
            ${config.iconBg}
            flex items-center justify-center
          `}>
            <div className={config.iconColor}>
              {displayIcon}
            </div>
          </div>
          <div className="text-gray-700">
            {typeof message === 'string' ? <p>{message}</p> : message}
          </div>
          {description && (
            <p className="text-sm text-gray-500 mt-2">
              {description}
            </p>
          )}
        </div>

        {/* Warning Message */}
        {showWarning && finalWarningMessage && (
          <div className={`
            p-4 rounded-xl border text-sm
            ${config.warningBg}
            ${config.warningBorder}
            ${config.warningText}
          `}>
            {finalWarningMessage}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            variant={config.buttonVariant}
            onClick={onConfirm}
            isLoading={isLoading}
            icon={variant === 'danger' ? <Trash2 size={18} /> : undefined}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmModal;


